/*
 * Copyright (C) 2019 SADE Innovations Oy - All Rights Reserved
 *
 * NOTICE: This software is owned by SADE Innovations Oy and licensed under SADE Booster license.
 * All dissemination, usage, modification, copying, reproduction, selling and distribution of the
 * software and its intellectual and technical concepts are strictly forbidden without a valid license.
 * Such license can be obtained by issuing a SADE Booster License agreement from SADE Innovations Oy
 * (https://sadeinnovations.com).
 *
 */

import React, { Component, Fragment, ReactNode } from "react";
import NotificationsIcon from "@material-ui/icons/Notifications";
import StopIcon from "@material-ui/icons/Stop";
import AddIcon from "@material-ui/icons/Add";
import RefreshIcon from "@material-ui/icons/Refresh";
import MUIDataTable, { MUIDataTableColumnDef, MUIDataTableOptions, SelectableRows } from "mui-datatables";
import { IconButton, Typography } from "@material-ui/core";
import ConfirmationDialog from "../../../ui/confirmation-dialog";
import Loader from "../../../ui/loader";
import AddBatchUpdatePopup from "./add-batch-update-popup";
import { translations } from "../../../../generated/translationHelper";
import {
  AuthWrapper,
  EventDefinition,
  Maybe,
  Organization,
  OtaBatchUpdateState,
  OtaExecution,
  OtaManager,
  OtaUpdateObserver,
} from "@sade/data-access";

type OtaExecutionsTableCell = string | number | StopCell | JSX.Element;
type OtaExecutionsTableRow = OtaExecutionsTableCell[];

interface Props {
  organization: Organization;
}

interface State {
  loading: boolean;
  eventDefinitions: EventDefinition[];
  tableData: OtaExecutionsTableRow[];
  stopExecutionAction?: StopCell;
  errorMsg?: string;
  showAddBatchUpdatePopup: boolean;
}

interface StopCell {
  execution: OtaExecution;
}

export default class OtaManagement extends Component<Props, State> implements OtaUpdateObserver {
  private readonly tableColumns: MUIDataTableColumnDef[] = [
    {
      name: "id",
      label: "ID",
      options: {
        sort: true,
      },
    },
    {
      name: "startDate",
      label: translations.admin.texts.startDate(),
      options: {
        sort: true,
      },
    },
    {
      name: "stopDate",
      label: translations.admin.texts.stopDate(),
      options: {
        sort: true,
      },
    },
    {
      name: "progress",
      label: translations.admin.texts.progress(),
      options: {
        sort: true,
      },
    },
    {
      name: "status",
      label: translations.common.texts.status(),
      options: {
        sort: true,
      },
    },
    {
      name: "",
      label: "",
      options: {
        customBodyRender: (stop: StopCell): ReactNode => {
          if (stop.execution.executionStatus === "RUNNING") {
            return (
              <div
                onClick={async (): Promise<void> => {
                  this.setState({ stopExecutionAction: { execution: stop.execution } });
                }}
              >
                <IconButton
                  title={translations.common.buttons.remove()}
                  style={{ backgroundColor: "#e6e6e6" }}
                  size="small"
                >
                  <StopIcon />
                </IconButton>
              </div>
            );
          }
        },
      },
    },
  ];

  private readonly tableOptions: MUIDataTableOptions = {
    filterType: "checkbox",
    selectableRows: "none" as SelectableRows,
    selectableRowsOnClick: false,
    search: false,
    download: false,
    print: false,
    filter: false,
    viewColumns: false,
    selectToolbarPlacement: "none",
    textLabels: {
      body: {
        noMatch: translations.admin.texts.noUpdateExecutionsAvailable(),
        toolTip: translations.common.texts.sort(),
      },
      pagination: {
        next: translations.common.buttons.nextPage(),
        previous: translations.common.buttons.previousPage(),
        rowsPerPage: translations.common.inputs.rowsPerPage(),
        displayRows: translations.common.texts.of(),
      },
    },
  };

  public constructor(props: Props) {
    super(props);
    this.state = {
      loading: false,
      eventDefinitions: [],
      tableData: [],
      showAddBatchUpdatePopup: false,
    };
  }

  private async getCurrentUserOrganization(): Promise<Maybe<string>> {
    const claims = await AuthWrapper.getCurrentAuthenticatedUserClaims();
    return claims?.homeOrganizationId;
  }

  public async componentDidMount(): Promise<void> {
    await OtaManager.getInstance().init(await this.getCurrentUserOrganization());
    await this.performLoad(this.updateTableData());
    OtaManager.getInstance().addObserver(this);
  }

  public async componentWillUnmount(): Promise<void> {
    OtaManager.getInstance().removeObserver(this);
    await OtaManager.getInstance().uninit();
  }

  public async onOtaBatchUpdateState(_state: OtaBatchUpdateState): Promise<void> {
    this.updateTableData(true);
  }

  private async performLoad(...promises: Promise<void>[]): Promise<void> {
    this.setState({ loading: true });

    try {
      await Promise.all(promises);
    } catch (err) {
      console.error("performLoad", err);
    }
    this.setState({ loading: false });
  }

  private async getOtaBatchExecutions(forceUpdate?: boolean): Promise<OtaExecution[]> {
    return OtaManager.getInstance().getOtaBatchExecutions(forceUpdate);
  }

  private async updateTableData(forceUpdate?: boolean): Promise<void> {
    const otaExecutions: OtaExecution[] = await this.getOtaBatchExecutions(forceUpdate);

    const tableData: OtaExecutionsTableRow[] = otaExecutions.map((execution) => {
      return [
        execution.id,
        execution.startDate ?? "-",
        execution.stopDate ?? "-",
        execution.currentCount + "/" + execution.maxCount,
        execution.executionStatus ?? translations.common.texts.N_A(),
        { execution },
      ];
    });

    this.setState({ tableData });
  }

  private handleConfirmStopExecution = async (): Promise<void> => {
    if (!this.state.stopExecutionAction) {
      return;
    }

    this.setState({ loading: true, stopExecutionAction: undefined });
    try {
      await OtaManager.getInstance().stopOtaBatchExecution(this.state.stopExecutionAction.execution.id);
    } catch (error) {
      console.error("stopOtaBatchExecution", error);
      this.setState({
        errorMsg: translations.admin.texts.failedToDeleteSubscription(),
      });
    }
    this.setState({ loading: false });
  };

  private handleAddIconClick = async (): Promise<void> => {
    this.setState({ showAddBatchUpdatePopup: true });
  };

  private handleRefreshIconClick = async (): Promise<void> => {
    await this.performLoad(this.updateTableData(true));
  };

  private handleClosePopup = (): void => {
    this.setState({
      showAddBatchUpdatePopup: false,
      errorMsg: undefined,
    });
  };

  private handleCancelStopExecution = (): void => {
    this.setState({ stopExecutionAction: undefined });
  };

  private renderBatchUpdateExecutionsTable(): ReactNode {
    const titleElement = (
      <div>
        <NotificationsIcon className="notification-organization-icon" />
        <Typography variant="h6">{translations.admin.texts.otaManagement()}</Typography>
      </div>
    );

    return (
      <Fragment>
        <MUIDataTable
          title={titleElement}
          data={this.state.tableData}
          columns={this.tableColumns}
          options={this.tableOptions}
        />
        <Loader show={this.state.loading} />
      </Fragment>
    );
  }

  private renderButtonsRow(): ReactNode {
    if (this.state.loading) return;
    return (
      <div className="notification-buttons-container">
        <IconButton
          title={translations.common.buttons.refresh()}
          onClick={this.handleRefreshIconClick}
          style={{ backgroundColor: "#e6e6e6", marginLeft: "3rem", marginRight: "2rem", marginTop: "1rem" }}
          size="small"
        >
          <RefreshIcon />
        </IconButton>
        <IconButton
          title={translations.common.buttons.add()}
          onClick={this.handleAddIconClick}
          style={{ backgroundColor: "#e6e6e6", marginLeft: "3rem", marginRight: "2rem", marginTop: "1rem" }}
          size="small"
        >
          <AddIcon />
        </IconButton>
      </div>
    );
  }

  private renderStopExecutionPopup(): ReactNode {
    if (!this.state.stopExecutionAction) {
      return;
    }
    return (
      <ConfirmationDialog
        title={translations.admin.texts.stopExecution()}
        message={translations.admin.texts.stop() + " " + this.state.stopExecutionAction.execution.id}
        onConfirm={this.handleConfirmStopExecution}
        onCancel={this.handleCancelStopExecution}
      />
    );
  }

  private renderAddBatchUpdatePopup(): ReactNode {
    return (
      <AddBatchUpdatePopup
        organization={this.props.organization}
        users={[]}
        events={this.state.eventDefinitions}
        open={this.state.showAddBatchUpdatePopup}
        onClose={async (): Promise<void> => {
          this.handleClosePopup();
        }}
      />
    );
  }

  public render(): JSX.Element {
    return (
      <Fragment>
        {this.renderBatchUpdateExecutionsTable()}
        {this.renderButtonsRow()}
        {this.renderAddBatchUpdatePopup()}
        {this.renderStopExecutionPopup()}
      </Fragment>
    );
  }
}
